import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../../../../_base-shared/contracts/laravel-response.interface';
import { CaseCreditor } from '../../../../../../../../_base-shared/models/Case/CaseCreditor';
import { PaymentPlanType } from '../../../../../../../../_base-shared/models/Product/PaymentPlanType';
import { MiscConfigService } from '../../../../config/misc-config/misc-config.service';
import { CreditorService } from '../../../../creditor/creditor.service';
import { CaseCreditorService } from '../../../case-creditor.service';

@Component({
  standalone: false,
  selector:    'app-calculation-modal',
  templateUrl: './calculation-modal.component.html',
  styleUrls:   ['./calculation-modal.component.scss']
})
export class CalculationModalComponent implements OnInit, OnDestroy {
  public caseCreditor: CaseCreditor;
  public isLoading = 0;
  public isSubmitting: boolean;
  public settlementBalance: number;
  public form: UntypedFormGroup;
  public serverResponse: LaravelResourceResponse;
  public paymentPlanTypes: Array<PaymentPlanType>;
  public topUpAmount: number;
  public caseId: number;
  public managementFee: number;

  private subscriptions: Array<Subscription> = [];

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: {
      caseCreditor: CaseCreditor,
      settlementBalance: number,
    },
    @Optional() public dialogRef: MatDialogRef<CalculationModalComponent>,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private toastr: ToastrService,
    private caseCreditorService: CaseCreditorService,
    private miscConfigService: MiscConfigService,
  ) {
  }

  ngOnInit(): void {
    if (this.data) {
      this.caseCreditor      = this.data.caseCreditor;
      this.settlementBalance = +this.data.settlementBalance;
      this.getManagementFee();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  private buildForm(caseCreditor: any, settlementBalance: number): void {
    this.form = this.fb.group({
      initial_balance:   [{value: caseCreditor.initial_balance, disabled: true}],
      negotiated_amount: [
        caseCreditor.negotiated_amount,
        [this.negotiatedAmountValidator(caseCreditor.initial_balance)]
      ],
      discount:          [{value: caseCreditor.initial_balance - caseCreditor.negotiated_amount, disabled: true}],
      available_funds:   [{value: settlementBalance, disabled: true}],
      top_up:            [{value: null, disabled: true}],
    });
    this.calculateTopUp();

    this.form.get('negotiated_amount').valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged())
      .subscribe((newNegotiatedAmount) => {
        this.calculateTopUp(newNegotiatedAmount);
      });
  }

  public calculateTopUp(newNegotiatedAmount: number = null): void {
    // console.log(newNegotiatedAmount);
    const currentBalance   = +this.form.get('initial_balance').value;
    const negotiatedAmount = +(newNegotiatedAmount ? newNegotiatedAmount : this.form.get('negotiated_amount').value);
    // console.log(currentBalance);
    // console.log(negotiatedAmount);

    if (currentBalance != null && negotiatedAmount != null) {
      const discount    = currentBalance - negotiatedAmount;
      const topUpAmount = (negotiatedAmount - this.settlementBalance) + this.managementFee + (discount * 0.25);
      this.form.get('discount').patchValue(discount);
      this.form.get('top_up').patchValue(topUpAmount);
    }
  }

  private negotiatedAmountValidator(currentBalance: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const negotiatedAmount = control.value;
      if (negotiatedAmount != null && negotiatedAmount > currentBalance) {
        return {negotiatedTooHigh: true};
      }
      return null;
    };
  }


  public submitForm(form: UntypedFormGroup): void {
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }
    const data = {
      negotiated_amount: +this.form.get('negotiated_amount').value,
      creditor_id:       this.caseCreditor.creditor_id,
      debt_amount:       this.caseCreditor.debt_amount,
    };

    this.isLoading++;
    this.subscriptions.push(
      this.caseCreditorService.updateCaseCreditor(this.caseCreditor.case_id, this.caseCreditor.id, data)
        .pipe(finalize(() => this.isLoading--))
        .subscribe({
          next:  res => {
            this.toastr.success(this.translate.instant('SHARED.success'));
            this.dialogRef.close();
          },
          error: err => {
            if (err.error?.message) {
              this.toastr.error(err.error.message);
            } else {
              this.toastr.error(this.translate.instant('SHARED.error'));
            }
          }
        })
    );
  }

  public getManagementFee(): void {
    this.isLoading++;
    this.subscriptions.push(
      this.miscConfigService.getConfigDataWithKey('ds_default_fee_amount')
        .pipe(finalize(() => this.isLoading--))
        .subscribe({
          next:  res => {
            this.managementFee = +res.data.value;
            this.buildForm(this.caseCreditor, this.settlementBalance);
          },
          error: err => {
            this.toastr.error(this.translate.instant('SHARED.error'));
          }
        })
    );
  }
}
